import { OrganizationApi } from "@genesys/cloud-frontend/apis/OrganizationApi";
import type { LimitsEntityListing } from "@genesys/cloud-frontend/models";
import { API_CONFIGURATION } from "./apiUtils";

/** Namespace for Quality outer limits (organization limits API). */
export const QUALITY_OUTER_LIMITS_NAMESPACE = "quality";

/** Known limit keys under the quality namespace; extend as you add callers. */
export const QualityOuterLimitKey = {
    EvaluationFormAiScoringQuestionsMax:
        "evaluation.form.ai.scoring.questions.max",
    AgentScoringRulesMax: "agent.scoring.rules.max",
} as const;

export type QualityOuterLimitKeyId =
    typeof QualityOuterLimitKey[keyof typeof QualityOuterLimitKey];

/**
 * Reads a single numeric outer limit from an organization limits namespace listing.
 */
export function getLimitValueFromListing(
    listing: LimitsEntityListing | undefined,
    limitKey: string
): number | undefined {
    return listing?.entities?.find((entity) => entity.key === limitKey)?.value;
}

/**
 * Fetches effective outer limits for the quality namespace via Organization API
 * (`GET .../organizations/limits/namespaces/{namespaceName}`), then returns the value
 * for the requested limit key, if present.
 */
export async function fetchQualityOrganizationOuterLimit(
    limitKey: string,
    configuration = API_CONFIGURATION
): Promise<number | undefined> {
    try {
        const organizationApi = new OrganizationApi(configuration);
        const response = await organizationApi.getOrganizationsLimitsNamespace({
            namespaceName: QUALITY_OUTER_LIMITS_NAMESPACE,
        });
        return getLimitValueFromListing(response, limitKey);
    } catch (error) {
        console.error(
            `Failed to fetch outer limit for key "${limitKey}":`,
            error
        );
        throw error;
    }
}

/**
 * Convenience for the evaluation form AI scoring questions max outer limit.
 */
export async function getEvaluationFormAiScoringQuestionsMaxOuterLimit(
    configuration = API_CONFIGURATION
): Promise<number | undefined> {
    return fetchQualityOrganizationOuterLimit(
        QualityOuterLimitKey.EvaluationFormAiScoringQuestionsMax,
        configuration
    );
}

/**
 * Convenience for the max number of agent scoring rules allowed per program.
 */
export async function getAgentScoringRulesMaxOuterLimit(
    configuration = API_CONFIGURATION
): Promise<number | undefined> {
    return fetchQualityOrganizationOuterLimit(
        QualityOuterLimitKey.AgentScoringRulesMax,
        configuration
    );
}
